import { ONE_HOUR_MS, type Standard } from '@netpurpose/types'
import { valueIsDefined } from '@netpurpose/utils'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { pickBy } from 'lodash'
import { GeneratedFactApi } from '../../GeneratedApi'
import type {
  EntityWithEstimationAndSdg as EntityWithEstimationAndSDG,
  EntityWithEstimationsCoverage,
  HoldingWithCoverage,
  HoldingWithCoverageByCompany as HoldingWithCoverageByCompanyBE,
} from '../../generated/facts'
import type { ReverseFieldMap } from '../../queryBuilder'
import { type Camelize, snakeToCamelKeys } from '../../utils'
import { getPaginationConfig, useUrlApiConnector } from '../useUrlApiConnector'
import { type Entity, transformEntity } from './utils'

export type HoldingEntity = Entity & {
  hasResearch: boolean
  hasEstimatedData: boolean
  contributingRevenuePct?: number
  potentialRevenuePct?: number
  negativeRevenuePct?: number
}

export type HoldingFund = Camelize<HoldingWithCoverage['fund']>

export type Holding = Omit<Camelize<HoldingWithCoverage['holding']>, 'holdingId' | 'name'> & {
  id: number
  name: string
  entity?: HoldingEntity
  fund?: HoldingFund
}

export type HoldingWithCoverageByCompany = Camelize<HoldingWithCoverageByCompanyBE['holding']> & {
  entity?: HoldingEntity
}

export type HoldingWithFilters = Holding &
  HoldingWithCoverageByCompany & {
    'entity.name': string
    'entity.isCovered': boolean
    'entity.hasResearch': boolean
    'entity.alignedSdgs': Standard[]
    'entity.contributingRevenuePct': number
    'entity.potentialRevenuePct': number
    'entity.negativeRevenuePct': number
    searchTerm: string
  }

const tableToApiFieldMap: Partial<ReverseFieldMap<keyof HoldingWithFilters>> = {
  name: 'name',
  'entity.name': 'entity.name',
  securityIdentifier: 'security_identifier',
  weight: 'weight',
  'entity.isCovered': 'entity.is_covered',
  'entity.hasResearch': 'entity.has_research',
  'entity.alignedSdgs': 'sdg_goals',
  'entity.contributingRevenuePct': 'contributing_revenue_pct',
  'entity.potentialRevenuePct': 'potential_revenue_pct',
  'entity.negativeRevenuePct': 'negative_revenue_pct',
  searchTerm: 'search_term',
  holdingsCount: 'holdings_count',
}

const checkEntityHasProperty = (
  entity: unknown,
  property: string,
): entity is Camelize<EntityWithEstimationAndSDG> => {
  return typeof entity === 'object' && entity !== null && property in entity
}

const transformHoldingEntity = (
  entity: Camelize<EntityWithEstimationsCoverage | EntityWithEstimationAndSDG | undefined | null>,
) =>
  entity
    ? ({
        ...transformEntity(entity),
        hasEstimatedData: entity.hasEstimatedData,
        hasResearch: !!entity.hasResearch,
        ...(checkEntityHasProperty(entity, 'contributingRevenuePct')
          ? {
              contributingRevenuePct: entity.contributingRevenuePct,
              potentialRevenuePct: entity.potentialRevenuePct as number,
              negativeRevenuePct: entity.negativeRevenuePct as number,
            }
          : {}),
      } as Entity & {
        hasEstimatedData: boolean
        hasResearch: boolean
        contributingRevenuePct?: number | undefined
        potentialRevenuePct?: number | undefined
        negativeRevenuePct?: number | undefined
      })
    : undefined

const transformHoldingWithCoverage = <HoldingProps extends Camelize<HoldingWithCoverage>>({
  entity,
  holding,
  fund,
  sdiClassification,
  sdiClassificationConfidence,
}: HoldingProps): Holding => {
  const transformedHoldingEntity = transformHoldingEntity(entity)
  const { holdingId, portfolioId, snapshotId, name, securityIdentifier, identifierType, ...rest } =
    snakeToCamelKeys(holding)

  return {
    ...pickBy(rest, valueIsDefined),
    id: holdingId,
    portfolioId,
    snapshotId,
    name: name || '',
    ...(transformedHoldingEntity
      ? {
          entity: {
            ...transformedHoldingEntity,
            ...pickBy({ sdiClassification, sdiClassificationConfidence }, valueIsDefined),
          },
        }
      : {}),
    securityIdentifier,
    identifierType,
    fund,
  }
}

const transformHoldingWithCoverageByCompany = ({
  entity,
  holding,
}: HoldingWithCoverageByCompanyBE): HoldingWithCoverageByCompany => {
  const transformedHoldingEntity = entity
    ? transformHoldingEntity(snakeToCamelKeys(entity))
    : undefined

  return {
    ...snakeToCamelKeys(holding),
    ...(transformedHoldingEntity ? { entity: transformedHoldingEntity } : {}),
  }
}

export const PAGINATED_HOLDINGS_QUERY_CACHE_KEY = 'paginatedHoldings'

export const usePaginatedHoldings = ({
  portfolioId,
  enabled,
  defaultParams,
  defaultSortBy,
  hasSnapshotsAccess,
}: {
  portfolioId: number
  enabled: boolean
  defaultParams?: { snapshot_id?: number }
  defaultSortBy?: keyof HoldingWithFilters
  hasSnapshotsAccess?: boolean
}) => {
  const { queryString, filterConfig, initialPaginationConfig } =
    useUrlApiConnector<HoldingWithFilters>({
      tableToApiFieldMap,
      urlKey: PAGINATED_HOLDINGS_QUERY_CACHE_KEY,
      ...(defaultParams ? { defaultParams } : {}),
      ...(defaultSortBy ? { defaultSortBy } : {}),
    })

  const { data, ...rest } = useQuery({
    // include portfolioId for easier cache invalidation on ie., portfolio AUM update
    queryKey: [PAGINATED_HOLDINGS_QUERY_CACHE_KEY, queryString, portfolioId, hasSnapshotsAccess],
    queryFn: () =>
      GeneratedFactApi.portfolios.listPortfolioHoldings({
        path: { portfolio_id: portfolioId },
        query: { q: queryString },
        ...(hasSnapshotsAccess ? { headers: { 'x-np-feature': ['snapshot-attribution'] } } : {}),
      }),
    placeholderData: keepPreviousData,
    enabled,
    staleTime: ONE_HOUR_MS,
  })
  const holdings = data?.results?.map((holding) =>
    transformHoldingWithCoverage(snakeToCamelKeys(holding)),
  )

  const paginationConfig = getPaginationConfig({
    numResults: data?.total,
    paginationConfig: initialPaginationConfig,
  })

  return {
    ...rest,
    data: holdings,
    filterConfig,
    paginationConfig,
  }
}

export const PAGINATED_FOF_HOLDINGS_BY_COMPANY_QUERY_CACHE_KEY = 'paginatedFoFHoldingsByCompany'

export const useFoFHoldingsByCompany = ({
  portfolioId,
  enabled,
  defaultParams,
  defaultSortBy,
  hasSnapshotsAccess,
}: {
  portfolioId: number
  enabled: boolean
  defaultParams?: { snapshot_id?: number }
  defaultSortBy?: keyof HoldingWithFilters
  hasSnapshotsAccess?: boolean
}) => {
  const { queryString, filterConfig, initialPaginationConfig } =
    useUrlApiConnector<HoldingWithFilters>({
      tableToApiFieldMap,
      urlKey: PAGINATED_FOF_HOLDINGS_BY_COMPANY_QUERY_CACHE_KEY,
      ...(defaultParams ? { defaultParams } : {}),
      ...(defaultSortBy ? { defaultSortBy } : {}),
    })

  const { data, ...rest } = useQuery({
    queryKey: [
      PAGINATED_FOF_HOLDINGS_BY_COMPANY_QUERY_CACHE_KEY,
      queryString,
      portfolioId,
      hasSnapshotsAccess,
    ],
    queryFn: () =>
      GeneratedFactApi.portfolios.listPortfolioWithFundsHoldingsByCompany({
        path: { portfolio_id: portfolioId },
        query: { q: queryString },
        ...(hasSnapshotsAccess ? { headers: { 'x-np-feature': ['snapshot-attribution'] } } : {}),
      }),
    placeholderData: keepPreviousData,
    enabled,
    staleTime: ONE_HOUR_MS,
  })
  const holdings = data?.results?.map(transformHoldingWithCoverageByCompany)

  const paginationConfig = getPaginationConfig({
    numResults: data?.total,
    paginationConfig: initialPaginationConfig,
  })

  return {
    ...rest,
    data: holdings,
    filterConfig,
    paginationConfig,
  }
}
